import React, { useState, useRef } from 'react';
import { useTheme } from '../context/ThemeContext';
import { useLocation } from 'react-router-dom';
import { Sun, Moon } from 'lucide-react';

export default function FloatingThemeToggle() {
  const { theme, toggleTheme } = useTheme();
  const location = useLocation();
  
  // Custom drag state
  const [position, setPosition] = useState(null);
  const isDraggingRef = useRef(false);
  const dragStartRef = useRef({ x: 0, y: 0 });
  const buttonPositionRef = useRef({ x: 0, y: 0 });
  const buttonSizeRef = useRef({ width: 46, height: 46 });
  
  // Strip any trailing slash before comparing — "/become-a-fellow/" (with
  // slash) is a real URL users land on, and a strict equality check missed
  // it, rendering the toggle on a page that must stay light-mode only.
  const path = location.pathname.replace(/\/+$/, '') || '/';
  if (
    path === '/nominations' ||
    path === '/nominations/portal' ||
    path === '/become-a-fellow'
  ) {
    return null;
  }

  const isGeneratorPage = location.pathname === '/generator' || location.pathname === '/generator/';
  
  const handleMouseDown = (e) => {
    // Only drag with left click
    if (e.button !== 0) return;
    
    isDraggingRef.current = false;
    dragStartRef.current = { x: e.clientX, y: e.clientY };
    
    const rect = e.currentTarget.getBoundingClientRect();
    buttonSizeRef.current = { width: rect.width, height: rect.height };
    buttonPositionRef.current = {
      x: position ? position.x : rect.left,
      y: position ? position.y : rect.top
    };
    
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  };
  
  const handleMouseMove = (e) => {
    const deltaX = e.clientX - dragStartRef.current.x;
    const deltaY = e.clientY - dragStartRef.current.y;
    
    if (Math.abs(deltaX) > 8 || Math.abs(deltaY) > 8) {
      isDraggingRef.current = true;
    }
    
    if (isDraggingRef.current) {
      const newX = buttonPositionRef.current.x + deltaX;
      const newY = buttonPositionRef.current.y + deltaY;
      
      // Prevent dragging off screen
      const sizeW = buttonSizeRef.current?.width || 46;
      const sizeH = buttonSizeRef.current?.height || 46;
      const maxW = window.innerWidth - sizeW - 10;
      const maxH = window.innerHeight - sizeH - 10;
      const boundedX = Math.max(10, Math.min(maxW, newX));
      const boundedY = Math.max(10, Math.min(maxH, newY));
      
      setPosition({ x: boundedX, y: boundedY });
    }
  };
  
  const handleMouseUp = (e) => {
    document.removeEventListener('mousemove', handleMouseMove);
    document.removeEventListener('mouseup', handleMouseUp);
  };
  
  const handleTouchStart = (e) => {
    if (e.touches.length !== 1) return;
    const touch = e.touches[0];
    
    isDraggingRef.current = false;
    dragStartRef.current = { x: touch.clientX, y: touch.clientY };
    
    const rect = e.currentTarget.getBoundingClientRect();
    buttonSizeRef.current = { width: rect.width, height: rect.height };
    buttonPositionRef.current = {
      x: position ? position.x : rect.left,
      y: position ? position.y : rect.top
    };
    
    document.addEventListener('touchmove', handleTouchMove, { passive: false });
    document.addEventListener('touchend', handleTouchEnd);
  };
  
  const handleTouchMove = (e) => {
    if (e.touches.length !== 1) return;
    const touch = e.touches[0];
    
    const deltaX = touch.clientX - dragStartRef.current.x;
    const deltaY = touch.clientY - dragStartRef.current.y;
    
    if (Math.abs(deltaX) > 8 || Math.abs(deltaY) > 8) {
      isDraggingRef.current = true;
    }
    
    if (isDraggingRef.current) {
      if (e.cancelable) e.preventDefault(); // Stop mobile background scrolling
      
      const newX = buttonPositionRef.current.x + deltaX;
      const newY = buttonPositionRef.current.y + deltaY;
      
      const sizeW = buttonSizeRef.current?.width || 38;
      const sizeH = buttonSizeRef.current?.height || 38;
      const maxW = window.innerWidth - sizeW - 10;
      const maxH = window.innerHeight - sizeH - 10;
      const boundedX = Math.max(10, Math.min(maxW, newX));
      const boundedY = Math.max(10, Math.min(maxH, newY));
      
      setPosition({ x: boundedX, y: boundedY });
    }
  };
  
  const handleTouchEnd = (e) => {
    document.removeEventListener('touchmove', handleTouchMove);
    document.removeEventListener('touchend', handleTouchEnd);
  };
  
  const handleClick = (e) => {
    if (isDraggingRef.current) {
      e.preventDefault();
      e.stopPropagation();
      return;
    }
    toggleTheme();
  };

  const getStyle = () => {
    const baseStyle = {
      position: 'fixed',
      zIndex: 99999, // Render on top of loaders & navigation
      cursor: isDraggingRef.current ? 'grabbing' : 'grab',
      touchAction: 'none',
      userSelect: 'none'
    };
    
    if (position) {
      return {
        ...baseStyle,
        left: `${position.x}px`,
        top: `${position.y}px`,
        bottom: 'auto',
        right: 'auto'
      };
    }
    
    return baseStyle;
  };

  return (
    <button
      className={`floating-theme-toggle ${theme} ${isGeneratorPage ? 'is-generator' : ''}`}
      style={getStyle()}
      onMouseDown={handleMouseDown}
      onTouchStart={handleTouchStart}
      onClick={handleClick}
    >
      {theme === 'dark' ? <Sun size={20} /> : <Moon size={20} />}
    </button>
  );
}
